import React, { useState, useEffect, useRef } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── CONSTANTS ──────────────────────────────────────────────────────────────
// Only the 3 atomic meeting types — no "BM+EGM" style combo buttons.
// Those used to insert the literal string "BM+EGM" as ONE array element,
// which breaks every downstream consumer (mca_meetings.meeting_type,
// Events Master's meeting-type dropdown) that only understands BM/AGM/EGM
// individually. Selecting AGM and EGM separately already achieves the
// same "allowed in either" meaning correctly.
const MEETING_TYPES = ['BM', 'AGM', 'EGM'];
// BOARD is not user-selectable — it's auto-applied whenever only BM is
// chosen. Ordinary/Special (Section 114, Companies Act 2013) is a member-
// resolution classification and never applies to a pure Board Resolution.
const RESOLUTION_TYPES = ['ORDINARY', 'SPECIAL'];
// Business classification (Section 102, Companies Act 2013) — a SEPARATE
// axis from resolution_type (Section 114 voting threshold) above. Ordinary
// Business is only the 4 standard AGM matters (accounts, dividend,
// director rotation, auditor); everything else at an AGM, and everything
// at an EGM, is Special Business — regardless of whether it's passed as
// an Ordinary or Special Resolution. Never applies to a Board Resolution.
const BUSINESS_CLASSIFICATIONS = [
  { value: 'ORDINARY_BUSINESS', label: 'Ordinary Business' },
  { value: 'SPECIAL_BUSINESS', label: 'Special Business' },
];
const CATEGORIES = [
  'Directors & KMP',
  'Share Capital',
  'Borrowings & Charges',
  'Registered Office',
  'Auditors & Accounts',
  'Investments & Loans',
  'Contracts & Agreements',
  'Dividend',
  'Other',
];
const MCA_FORMS = [
  'DIR-12', 'DIR-3 KYC', 'INC-22', 'INC-20A',
  'SH-7', 'SH-8', 'CHG-1', 'CHG-4',
  'MGT-14', 'ADT-1', 'MBP-1', 'None',
];

const NAV = { color: '#0f1f45' };

const pill = (label, color) => ({
  display: 'inline-flex', alignItems: 'center',
  padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
  background: color === 'blue' ? '#e8edf7' : color === 'amber' ? '#fef3cd' : color === 'green' ? '#e6f4ea' : color === 'red' ? '#fde8e8' : '#f0f0f0',
  color: color === 'blue' ? '#0f1f45' : color === 'amber' ? '#92600a' : color === 'green' ? '#1a7340' : color === 'red' ? '#b91c1c' : '#555',
});

// ── ICONS ──────────────────────────────────────────────────────────────────
const Icon = {
  plus: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  search: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  edit: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>,
  close: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  eye: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  filter: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  chevronDown: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="6 9 12 15 18 9"/></svg>,
  warning: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
};

// ── TEMPLATE VARIABLE HELPERS ────────────────────────────────────────────
// Scans a template body for every {{token}} used, and separately for any
// {{#if field=="X"}}...{{#elif field=="Y"}}...{{/if}} conditional field
// and the option labels its branches use. This is what actually renders
// under the editor — always accurate to what's really in the text,
// unlike a fixed preset button list.
function detectTemplateVariables(templateBody) {
  const text = templateBody || '';
  const plain = [...new Set((text.match(/\{\{(\w+)\}\}/g) || []).map(m => m.replace(/[{}]/g, '')))];
  const choiceRegex = /\{\{#(?:if|elif)\s+(\w+)\s*==\s*"([^"]+)"\}\}/g;
  const choices = {};
  let m;
  while ((m = choiceRegex.exec(text)) !== null) {
    if (!choices[m[1]]) choices[m[1]] = [];
    if (!choices[m[1]].includes(m[2])) choices[m[1]].push(m[2]);
  }
  return { plain, choices };
}

// ── EMPTY STATE ──────────────────────────────────────────────────────────
function EmptyState({ onAdd }) {
  return (
    <div style={{ textAlign: 'center', padding: '72px 24px' }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: '#e8edf7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0f1f45" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
      </div>
      <p style={{ fontSize: 15, fontWeight: 600, color: '#0f1f45', margin: '0 0 6px' }}>No resolutions yet</p>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px' }}>Add your first resolution template to power the event filing engine.</p>
      <button onClick={onAdd} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, background: '#0f1f45', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        {Icon.plus} Add resolution
      </button>
    </div>
  );
}

// ── RESOLUTION FORM MODAL ─────────────────────────────────────────────────
function ResolutionFormModal({ resolution, onSave, onClose }) {
  const isEdit = !!resolution?.id;
  // required_attachments comes back from the API as a real array
  // (resolutions_master stores it as text[]) — the input field below
  // works with a comma-separated string, so normalize it here once,
  // on the way in, rather than handling both shapes everywhere else.
  const initialAttachments = Array.isArray(resolution?.required_attachments)
    ? resolution.required_attachments.join(', ')
    : (resolution?.required_attachments || '');
  const [form, setForm] = useState({
    resolution_code: '',
    resolution_title: '',
    category: CATEGORIES[0],
    resolution_type: 'BOARD',
    business_classification: null,
    meeting_type_allowed: ['BM'],
    cannot_be_in: [],
    mca_form_triggered: 'None',
    event_tag: '',
    template_body: '',
    explanatory_statement: '',
    required_attachments: '',
    notes: '',
    is_active: true,
    ...resolution,
    meeting_type_allowed: resolution?.meeting_type_allowed || ['BM'],
    cannot_be_in: resolution?.cannot_be_in || [],
    required_attachments: initialAttachments,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const textareaRef = useRef(null);
  const [customVarName, setCustomVarName] = useState('');
  const [showCondBuilder, setShowCondBuilder] = useState(false);
  const [condField, setCondField] = useState('');
  const [condOptions, setCondOptions] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleArr = (key, val) => {
    setForm(f => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
    });
  };

  // BM and AGM/EGM can never both be selected on one resolution — a
  // Board Resolution and a member resolution are separate legal acts.
  // Selecting one clears the other, and resolution_type auto-corrects:
  // BM-only → BOARD, AGM/EGM → defaults to ORDINARY if it was BOARD.
  const toggleMeetingType = (val) => {
    setForm(f => {
      const isGeneral = ['AGM', 'EGM'].includes(val);
      let arr = f.meeting_type_allowed || [];
      if (arr.includes(val)) {
        arr = arr.filter(x => x !== val);
      } else if (val === 'BM') {
        arr = ['BM'];
      } else if (isGeneral) {
        arr = [...arr.filter(x => x !== 'BM'), val];
      }
      const hasBM = arr.includes('BM');
      const hasAGM = arr.includes('AGM');
      const hasEGM = arr.includes('EGM');
      const hasGeneral = hasAGM || hasEGM;
      let resolution_type = f.resolution_type;
      if (hasBM && !hasGeneral) resolution_type = 'BOARD';
      else if (hasGeneral && !hasBM && resolution_type === 'BOARD') resolution_type = 'ORDINARY';
      // Business classification (Sec 102) — orthogonal to resolution_type
      // above. BM has none; EGM-only is always Special Business by
      // definition (an EGM has no Ordinary Business); AGM needs an
      // explicit pick, defaulted to Special Business (only 4 specific
      // matters are ever Ordinary Business, so that's the safer default).
      let business_classification = f.business_classification;
      if (hasBM && !hasGeneral) business_classification = null;
      else if (hasEGM && !hasAGM) business_classification = 'SPECIAL_BUSINESS';
      else if (hasAGM && !business_classification) business_classification = 'SPECIAL_BUSINESS';
      else if (!hasGeneral) business_classification = null;
      return { ...f, meeting_type_allowed: arr, resolution_type, business_classification };
    });
  };

  // Inserts text at the cursor position instead of always appending to
  // the end — actually usable when editing mid-document.
  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) { set('template_body', form.template_body + text); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    const next = form.template_body.slice(0, start) + text + form.template_body.slice(end);
    set('template_body', next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + text.length;
    });
  };

  const addCustomVariable = () => {
    const name = customVarName.trim().replace(/\s+/g, '_').replace(/[^\w]/g, '');
    if (!name) return;
    insertAtCursor(`{{${name}}}`);
    setCustomVarName('');
  };

  const insertConditional = () => {
    const field = condField.trim().replace(/\s+/g, '_').replace(/[^\w]/g, '');
    const opts = condOptions.split(',').map(s => s.trim()).filter(Boolean);
    if (!field || opts.length < 2) return;
    const skeleton = opts.map((opt, i) =>
      i === 0 ? `{{#if ${field}=="${opt}"}}\n\n` : `{{#elif ${field}=="${opt}"}}\n\n`
    ).join('') + '{{/if}}';
    insertAtCursor(skeleton);
    setCondField(''); setCondOptions(''); setShowCondBuilder(false);
  };

  const detected = detectTemplateVariables(form.template_body);
  const hasBM = (form.meeting_type_allowed || []).includes('BM');

  const validate = () => {
    const e = {};
    if (!form.resolution_code.trim()) e.resolution_code = 'Required';
    if (!form.resolution_title.trim()) e.resolution_title = 'Required';
    if (!form.template_body.trim()) e.template_body = 'Required';
    if (!form.event_tag.trim()) e.event_tag = 'Required';
    if (form.meeting_type_allowed.length === 0) e.meeting_type_allowed = 'Select at least one';
    if (!hasBM && form.meeting_type_allowed.length > 0 && !form.business_classification) e.business_classification = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const method = isEdit ? 'PUT' : 'POST';
      const url = isEdit
        ? `${BASE}/api/mca/resolutions/${resolution.id}`
        : `${BASE}/api/mca/resolutions`;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          required_attachments: form.required_attachments
            ? form.required_attachments.split(',').map(s => s.trim()).filter(Boolean)
            : [],
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      onSave(data);
    } catch (err) {
      setErrors({ general: err.message });
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5, display: 'block' };
  const inputStyle = { width: '100%', padding: '8px 11px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13, color: '#0f1f45', background: '#fff', boxSizing: 'border-box', outline: 'none' };
  const errStyle = { fontSize: 11, color: '#b91c1c', marginTop: 3 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 };
  const chkBtn = (active) => ({
    padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: active ? '#0f1f45' : '#f1f5f9', color: active ? '#fff' : '#64748b',
    transition: 'all 0.15s',
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,69,0.35)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 700, boxShadow: '0 24px 60px rgba(0,0,0,0.15)', marginBottom: 24 }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontSize: 16, fontWeight: 700, color: '#0f1f45', margin: 0 }}>{isEdit ? 'Edit resolution' : 'New resolution'}</p>
            <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Define rules, meeting type, and document template</p>
          </div>
          <button onClick={onClose} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#64748b' }}>{Icon.close}</button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>
          {errors.general && (
            <div style={{ background: '#fde8e8', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', color: '#b91c1c', fontSize: 13 }}>
              {Icon.warning} {errors.general}
            </div>
          )}

          {/* Row 1: Code + Title */}
          <div style={row2}>
            <div>
              <label style={labelStyle}>Resolution code *</label>
              <input style={{ ...inputStyle, borderColor: errors.resolution_code ? '#fca5a5' : '#e2e8f0' }}
                placeholder="e.g. RES-DIR-001"
                value={form.resolution_code}
                onChange={e => set('resolution_code', e.target.value)} />
              {errors.resolution_code && <p style={errStyle}>{errors.resolution_code}</p>}
            </div>
            <div>
              <label style={labelStyle}>Event tag * <span style={{ fontWeight: 400, color: '#94a3b8' }}>(slug)</span></label>
              <input style={{ ...inputStyle, borderColor: errors.event_tag ? '#fca5a5' : '#e2e8f0' }}
                placeholder="e.g. director_appointment"
                value={form.event_tag}
                onChange={e => set('event_tag', e.target.value.toLowerCase().replace(/\s+/g, '_'))} />
              {errors.event_tag && <p style={errStyle}>{errors.event_tag}</p>}
            </div>
          </div>

          {/* Title */}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Resolution title *</label>
            <input style={{ ...inputStyle, borderColor: errors.resolution_title ? '#fca5a5' : '#e2e8f0' }}
              placeholder="e.g. Appointment of Additional Director u/s 161"
              value={form.resolution_title}
              onChange={e => set('resolution_title', e.target.value)} />
            {errors.resolution_title && <p style={errStyle}>{errors.resolution_title}</p>}
          </div>

          {/* Row 2: Category + MCA Form */}
          <div style={{ ...row2, marginTop: 16 }}>
            <div>
              <label style={labelStyle}>Category</label>
              <select style={inputStyle} value={form.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>MCA form triggered</label>
              <select style={inputStyle} value={form.mca_form_triggered} onChange={e => set('mca_form_triggered', e.target.value)}>
                {MCA_FORMS.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
          </div>

          {/* Meeting Types Allowed — comes first now, since it drives Resolution Type below */}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Meeting type allowed * <span style={{ fontWeight: 400, color: '#94a3b8' }}>(select all that apply)</span></label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {MEETING_TYPES.map(t => (
                <button key={t} style={chkBtn(form.meeting_type_allowed.includes(t))} onClick={() => toggleMeetingType(t)}>{t}</button>
              ))}
            </div>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>BM and AGM/EGM can't be mixed on one resolution — selecting one clears the other. A matter needing both gets two resolutions, linked to the same Event.</p>
            {errors.meeting_type_allowed && <p style={errStyle}>{errors.meeting_type_allowed}</p>}
          </div>

          {/* Resolution Type — auto BOARD for BM-only, manual Ordinary/Special otherwise */}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Resolution type *</label>
            {hasBM ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#e8edf7', borderRadius: 7, width: 'fit-content' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#0f1f45' }}>BOARD RESOLUTION</span>
                <span style={{ fontSize: 11, color: '#64748b' }}>— Section 114 (Ordinary/Special) applies only to member resolutions, not Board decisions</span>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                {RESOLUTION_TYPES.map(t => (
                  <button key={t} style={chkBtn(form.resolution_type === t)} onClick={() => set('resolution_type', t)}>{t}</button>
                ))}
                {form.resolution_type === 'SPECIAL' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8, padding: '4px 10px', background: '#fef3cd', borderRadius: 6 }}>
                    {Icon.warning}
                    <span style={{ fontSize: 11, color: '#92600a', fontWeight: 600 }}>75% majority required (Sec. 114(2))</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Business Classification (Sec 102) — separate axis from
              Resolution Type above (Sec 114 voting threshold). Ordinary
              Business is only the 4 standard AGM matters; everything else
              at AGM, and everything at EGM, is Special Business. */}
          {!hasBM && (
            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>Business classification (Sec 102) *</label>
              {(form.meeting_type_allowed.includes('EGM') && !form.meeting_type_allowed.includes('AGM')) ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#fde8e8', borderRadius: 7, width: 'fit-content' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>SPECIAL BUSINESS</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>— an EGM has no Ordinary Business under Sec 102</span>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  {BUSINESS_CLASSIFICATIONS.map(bc => (
                    <button key={bc.value} style={chkBtn(form.business_classification === bc.value)} onClick={() => set('business_classification', bc.value)}>{bc.label}</button>
                  ))}
                </div>
              )}
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Ordinary Business is only the 4 standard AGM matters (accounts, dividend, director rotation, auditor appointment). Everything else discussed at an AGM is Special Business — independent of whether it's an Ordinary or Special Resolution above.</p>
              {errors.business_classification && <p style={errStyle}>{errors.business_classification}</p>}
            </div>
          )}

          {/* Cannot be in */}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Cannot be passed in <span style={{ fontWeight: 400, color: '#94a3b8' }}>(restriction)</span></label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['BM', 'AGM', 'EGM'].map(t => (
                <button key={t} style={{ ...chkBtn(form.cannot_be_in.includes(t)), background: form.cannot_be_in.includes(t) ? '#fde8e8' : '#f1f5f9', color: form.cannot_be_in.includes(t) ? '#b91c1c' : '#64748b' }}
                  onClick={() => toggleArr('cannot_be_in', t)}>{t}</button>
              ))}
            </div>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>If marked, system will block this resolution from being added to that meeting type.</p>
          </div>

          {/* Required Attachments */}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Required attachments <span style={{ fontWeight: 400, color: '#94a3b8' }}>(comma-separated)</span></label>
            <input style={inputStyle}
              placeholder="e.g. Consent letter, DIR-2, ID proof, Address proof"
              value={form.required_attachments}
              onChange={e => set('required_attachments', e.target.value)} />
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>These will appear as checklist items in the event filing module.</p>
          </div>

          {/* Template Body */}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Resolution template body *</label>

            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>Quick insert (common variables)</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['{{company_name}}', '{{director_name}}', '{{DIN}}', '{{date}}', '{{designation}}', '{{address}}', '{{amount}}', '{{percentage}}'].map(v => (
                  <button key={v} onClick={() => insertAtCursor(v)}
                    style={{ padding: '3px 9px', borderRadius: 5, border: '1px dashed #cbd5e1', background: '#fff', fontSize: 11, color: '#0f1f45', cursor: 'pointer', fontFamily: 'monospace' }}>{v}</button>
                ))}
              </div>

              <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 0 6px' }}>
                Custom variable <span style={{ fontWeight: 400, textTransform: 'none' }}>— not limited to the list above, type any name your resolution needs</span>
              </p>
              <div style={{ display: 'flex', gap: 6 }}>
                <input value={customVarName} onChange={e => setCustomVarName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomVariable())}
                  placeholder="e.g. proof_type, roc_jurisdiction"
                  style={{ ...inputStyle, padding: '5px 9px', fontSize: 12, fontFamily: 'monospace', flex: 1 }} />
                <button onClick={addCustomVariable}
                  style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: '#0f1f45', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>Insert</button>
              </div>

              <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 0 6px' }}>
                Conditional wording <span style={{ fontWeight: 400, textTransform: 'none' }}>— different text depending on a choice made when the case is created</span>
              </p>
              {!showCondBuilder ? (
                <button onClick={() => setShowCondBuilder(true)}
                  style={{ padding: '5px 12px', borderRadius: 6, border: '1px dashed #cbd5e1', background: '#fff', fontSize: 12, color: '#0f1f45', cursor: 'pointer' }}>+ Add conditional block</button>
              ) : (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, padding: 10 }}>
                  <input value={condField} onChange={e => setCondField(e.target.value)}
                    placeholder="Field name, e.g. proof_type"
                    style={{ ...inputStyle, padding: '5px 9px', fontSize: 12, fontFamily: 'monospace', marginBottom: 6 }} />
                  <input value={condOptions} onChange={e => setCondOptions(e.target.value)}
                    placeholder="Options, comma-separated, e.g. Rent Agreement, Authorization Letter"
                    style={{ ...inputStyle, padding: '5px 9px', fontSize: 12, marginBottom: 8 }} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={insertConditional}
                      style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: '#0f1f45', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Insert Block</button>
                    <button onClick={() => { setShowCondBuilder(false); setCondField(''); setCondOptions(''); }}
                      style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: 12, color: '#64748b', cursor: 'pointer' }}>Cancel</button>
                  </div>
                  <p style={{ fontSize: 10, color: '#94a3b8', margin: '8px 0 0' }}>This drops in an empty block for each option — fill each one with the matching wording. Whoever creates a case from this resolution will see "{condField || 'field name'}" as a dropdown with the options you typed above.</p>
                </div>
              )}
            </div>

            <textarea
              ref={textareaRef}
              rows={8}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: '"Plus Jakarta Sans", sans-serif', lineHeight: 1.7, borderColor: errors.template_body ? '#fca5a5' : '#e2e8f0' }}
              placeholder={"RESOLVED THAT pursuant to the provisions of Section 161 of the Companies Act, 2013, {{director_name}} (DIN: {{DIN}}), be and is hereby appointed as an Additional Director of the Company with effect from {{date}}..."}
              value={form.template_body}
              onChange={e => set('template_body', e.target.value)} />
            {errors.template_body && <p style={errStyle}>{errors.template_body}</p>}

            {(detected.plain.length > 0 || Object.keys(detected.choices).length > 0) && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#f0fdf4', borderRadius: 7 }}>
                <p style={{ fontSize: 10, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>
                  Variables detected in this resolution <span style={{ fontWeight: 400, textTransform: 'none' }}>— this is what the CS will actually fill in when creating a case</span>
                </p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {detected.plain.map(v => (
                    <span key={v} style={{ padding: '2px 8px', borderRadius: 5, background: '#dcfce7', fontSize: 11, color: '#166534', fontFamily: 'monospace' }}>{`{{${v}}}`}</span>
                  ))}
                  {Object.entries(detected.choices).map(([field, opts]) => (
                    <span key={field} style={{ padding: '2px 8px', borderRadius: 5, background: '#dbeafe', fontSize: 11, color: '#1e3a8a', fontFamily: 'monospace' }}>
                      {field} <span style={{ fontFamily: 'inherit', opacity: 0.7 }}>(dropdown: {opts.join(' / ')})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Explanatory Statement — Section 102, only relevant for a
              General Meeting resolution's notice; Board Meetings never
              carry one. Same {{token}} syntax as the resolution text above. */}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>
              Explanatory statement <span style={{ fontWeight: 400, color: '#94a3b8' }}>— Section 102, attached to the GM Notice for this resolution's item (leave blank for Board Resolutions). Same {'{{token}}'} syntax as above.</span>
            </label>
            <textarea
              rows={5}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: '"Plus Jakarta Sans", sans-serif', lineHeight: 1.7 }}
              placeholder={"The Board has recommended this resolution for the following reasons... None of the Directors or Key Managerial Personnel is concerned or interested in this resolution, except as stated above."}
              value={form.explanatory_statement}
              onChange={e => set('explanatory_statement', e.target.value)} />
          </div>

          {/* Notes */}
          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Internal notes <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
            <textarea rows={2} style={{ ...inputStyle, resize: 'none' }}
              placeholder="e.g. Section 161 — needs to be regularised at next AGM"
              value={form.notes}
              onChange={e => set('notes', e.target.value)} />
          </div>

          {/* Active toggle */}
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div onClick={() => set('is_active', !form.is_active)}
              style={{ width: 36, height: 20, borderRadius: 10, background: form.is_active ? '#0f1f45' : '#cbd5e1', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: form.is_active ? 19 : 3, transition: 'left 0.2s' }} />
            </div>
            <span style={{ fontSize: 13, color: '#374151' }}>{form.is_active ? 'Active — visible in event filing' : 'Inactive — hidden from event filing'}</span>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: 600, color: '#64748b', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? '#94a3b8' : '#0f1f45', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Add resolution'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DETAIL DRAWER ─────────────────────────────────────────────────────────
function DetailDrawer({ resolution, onEdit, onClose }) {
  const attachments = Array.isArray(resolution.required_attachments)
    ? resolution.required_attachments
    : (resolution.required_attachments ? resolution.required_attachments.split(',').map(s => s.trim()) : []);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 150, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(15,31,69,0.2)' }} />
      <div style={{ position: 'relative', width: 480, background: '#fff', boxShadow: '-8px 0 40px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 4px' }}>{resolution.resolution_code}</p>
            <p style={{ fontSize: 15, fontWeight: 700, color: '#0f1f45', margin: 0, lineHeight: 1.4 }}>{resolution.resolution_title}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => onEdit(resolution)} style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', fontSize: 12, fontWeight: 600, color: '#0f1f45', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>{Icon.edit} Edit</button>
            <button onClick={onClose} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#64748b' }}>{Icon.close}</button>
          </div>
        </div>

        <div style={{ padding: 24, flex: 1 }}>
          {/* Badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            <span style={pill(resolution.resolution_type, resolution.resolution_type === 'BOARD' ? 'green' : resolution.resolution_type === 'ORDINARY' ? 'blue' : 'amber')}>{resolution.resolution_type}</span>
            {(resolution.meeting_type_allowed || []).map(m => <span key={m} style={pill(m, 'green')}>{m}</span>)}
            {resolution.mca_form_triggered !== 'None' && <span style={pill(resolution.mca_form_triggered, 'red')}>{resolution.mca_form_triggered}</span>}
            {!resolution.is_active && <span style={pill('Inactive', '')}> Inactive</span>}
          </div>

          {/* Meta grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            {[
              ['Category', resolution.category],
              ['Event tag', resolution.event_tag],
              ['Cannot be in', (resolution.cannot_be_in || []).join(', ') || '—'],
              ['MCA form', resolution.mca_form_triggered],
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                <p style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, margin: '0 0 3px', textTransform: 'uppercase', letterSpacing: 0.5 }}>{k}</p>
                <p style={{ fontSize: 13, color: '#0f1f45', fontWeight: 600, margin: 0, fontFamily: resolution.event_tag === v ? 'monospace' : 'inherit' }}>{v || '—'}</p>
              </div>
            ))}
          </div>

          {/* Required attachments */}
          {attachments.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Required attachments</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {attachments.map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: '#f8fafc', borderRadius: 7, border: '1px solid #f1f5f9' }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#0f1f45', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: '#374151' }}>{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Template */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Resolution template</p>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: 13, color: '#374151', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
              {resolution.template_body}
            </div>
          </div>

          {/* Notes */}
          {resolution.notes && (
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes</p>
              <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, margin: 0 }}>{resolution.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────
export default function ResolutionMaster() {
  const [resolutions, setResolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [filterMeeting, setFilterMeeting] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [editResolution, setEditResolution] = useState(null);
  const [viewResolution, setViewResolution] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchResolutions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE}/api/mca/resolutions`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setResolutions(Array.isArray(data) ? data : []);
    } catch {
      setResolutions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchResolutions(); }, []);

  const handleSave = (saved) => {
    setResolutions(prev => {
      const exists = prev.find(r => r.id === saved.id);
      return exists ? prev.map(r => r.id === saved.id ? saved : r) : [saved, ...prev];
    });
    setShowForm(false);
    setEditResolution(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resolution? This cannot be undone.')) return;
    setDeleting(id);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${BASE}/api/mca/resolutions/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setResolutions(prev => prev.filter(r => r.id !== id));
      if (viewResolution?.id === id) setViewResolution(null);
    } finally {
      setDeleting(null);
    }
  };

  const openEdit = (r) => { setEditResolution(r); setShowForm(true); setViewResolution(null); };
  const openAdd = () => { setEditResolution(null); setShowForm(true); };

  const filtered = resolutions.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || r.resolution_title?.toLowerCase().includes(q) || r.resolution_code?.toLowerCase().includes(q) || r.event_tag?.toLowerCase().includes(q);
    const matchCat = filterCategory === 'All' || r.category === filterCategory;
    const matchType = filterType === 'All' || r.resolution_type === filterType;
    const matchMeeting = filterMeeting === 'All' || (r.meeting_type_allowed || []).includes(filterMeeting);
    return matchSearch && matchCat && matchType && matchMeeting;
  });

  const stats = {
    total: resolutions.length,
    ordinary: resolutions.filter(r => r.resolution_type === 'ORDINARY').length,
    special: resolutions.filter(r => r.resolution_type === 'SPECIAL').length,
    active: resolutions.filter(r => r.is_active).length,
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>MCA Workspace</span>
            <span style={{ color: '#cbd5e1' }}>›</span>
            <span style={{ fontSize: 11, color: '#0f1f45', fontWeight: 600 }}>Resolution Master</span>
          </div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: '#0f1f45', margin: 0 }}>Resolution Master</h1>
          <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Resolution templates that power the event filing engine</p>
        </div>
        <button onClick={openAdd}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: '#0f1f45', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {Icon.plus} Add resolution
        </button>
      </div>

      <div style={{ padding: '20px 24px' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
          {[
            { label: 'Total resolutions', value: stats.total, color: '#0f1f45' },
            { label: 'Ordinary', value: stats.ordinary, color: '#1a7340' },
            { label: 'Special', value: stats.special, color: '#92600a' },
            { label: 'Active', value: stats.active, color: '#0f1f45' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '14px 18px' }}>
              <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: s.color, margin: 0 }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '14px 18px', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 12px' }}>
            <span style={{ color: '#94a3b8' }}>{Icon.search}</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search resolutions…"
              style={{ border: 'none', background: 'transparent', fontSize: 13, color: '#0f1f45', outline: 'none', width: '100%' }} />
          </div>
          {[
            { label: 'Category', value: filterCategory, onChange: setFilterCategory, options: ['All', ...CATEGORIES] },
            { label: 'Type', value: filterType, onChange: setFilterType, options: ['All', ...RESOLUTION_TYPES] },
            { label: 'Meeting', value: filterMeeting, onChange: setFilterMeeting, options: ['All', ...MEETING_TYPES] },
          ].map(f => (
            <select key={f.label} value={f.value} onChange={e => f.onChange(e.target.value)}
              style={{ padding: '7px 28px 7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer', outline: 'none', appearance: 'none', minWidth: 130 }}>
              {f.options.map(o => <option key={o}>{o === 'All' ? `All ${f.label === 'Type' ? 'types' : f.label === 'Meeting' ? 'meetings' : 'categories'}` : o}</option>)}
            </select>
          ))}
          <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 14 }}>Loading resolutions…</div>
        ) : filtered.length === 0 && resolutions.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <EmptyState onAdd={openAdd} />
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['Code', 'Title & Category', 'Type', 'Meeting allowed', 'MCA form', 'Attachments', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No resolutions match your filters</td></tr>
                ) : filtered.map((r, i) => {
                  const attachments = Array.isArray(r.required_attachments)
                    ? r.required_attachments
                    : (r.required_attachments ? r.required_attachments.split(',').filter(Boolean) : []);
                  return (
                    <tr key={r.id} style={{ borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '13px 16px' }}>
                        <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, color: '#0f1f45', background: '#e8edf7', padding: '3px 8px', borderRadius: 5 }}>{r.resolution_code}</span>
                      </td>
                      <td style={{ padding: '13px 16px', maxWidth: 260 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#0f1f45', margin: '0 0 2px', lineHeight: 1.4 }}>{r.resolution_title}</p>
                        <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>{r.category} · <span style={{ fontFamily: 'monospace' }}>{r.event_tag}</span></p>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <span style={pill(r.resolution_type, r.resolution_type === 'BOARD' ? 'green' : r.resolution_type === 'ORDINARY' ? 'blue' : 'amber')}>{r.resolution_type}</span>
                        {r.business_classification && (
                          <p style={{ fontSize: 10, color: r.business_classification === 'ORDINARY_BUSINESS' ? '#0369a1' : '#b91c1c', margin: '3px 0 0', fontWeight: 600 }}>
                            {r.business_classification === 'ORDINARY_BUSINESS' ? 'Ordinary Business' : 'Special Business'}
                          </p>
                        )}
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(r.meeting_type_allowed || []).map(m => <span key={m} style={pill(m, 'green')}>{m}</span>)}
                        </div>
                        {(r.cannot_be_in || []).length > 0 && (
                          <p style={{ fontSize: 10, color: '#b91c1c', margin: '3px 0 0' }}>Not in: {r.cannot_be_in.join(', ')}</p>
                        )}
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        {r.mca_form_triggered !== 'None'
                          ? <span style={pill(r.mca_form_triggered, 'red')}>{r.mca_form_triggered}</span>
                          : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <span style={{ fontSize: 12, color: attachments.length ? '#374151' : '#cbd5e1' }}>
                          {attachments.length ? `${attachments.length} item${attachments.length > 1 ? 's' : ''}` : '—'}
                        </span>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: r.is_active ? '#1a7340' : '#94a3b8' }}>
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: r.is_active ? '#22c55e' : '#cbd5e1' }} />
                          {r.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button onClick={() => setViewResolution(r)} title="View"
                            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }}>{Icon.eye}</button>
                          <button onClick={() => openEdit(r)} title="Edit"
                            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#0f1f45', display: 'flex', alignItems: 'center' }}>{Icon.edit}</button>
                          <button onClick={() => handleDelete(r.id)} title="Delete" disabled={deleting === r.id}
                            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', cursor: 'pointer', color: '#b91c1c', display: 'flex', alignItems: 'center' }}>{Icon.trash}</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showForm && (
        <ResolutionFormModal
          resolution={editResolution}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditResolution(null); }}
        />
      )}
      {viewResolution && (
        <DetailDrawer
          resolution={viewResolution}
          onEdit={openEdit}
          onClose={() => setViewResolution(null)}
        />
      )}
    </div>
  );
}